import * as React from 'react';
import './RankedBars.css';

export interface RankedBarRow {
    name: string;
    /** Formatted for display, e.g. "50" or "12 min". */
    display: string;
    /** 0-100. */
    percent: number;
}

interface RankedBarsProps {
    rows: RankedBarRow[];
    emptyMessage?: string;
    /** A domain hue; the track is the same hue, lighter. */
    accent?: string;
}

/**
 * A ranked list of magnitudes: one hue, more-is-longer, every row directly
 * labelled. Sequential rather than categorical, because the rows are one
 * measure across counties -- they are not distinct series.
 */
const RankedBars: React.FC<RankedBarsProps> = (props: RankedBarsProps) => {
    if (props.rows.length === 0) {
        return <p className="ranked-bars__name">{props.emptyMessage ?? 'No data yet.'}</p>;
    }
    const style = props.accent ? ({'--accent': props.accent} as React.CSSProperties) : undefined;
    return (
        <div className="ranked-bars has-accent" style={style}>
            {props.rows.map((row, i) => {
                const percent = Math.max(0, Math.min(100, row.percent));
                return (
                    // Position, not name: two rows can legitimately share a
                    // label, and a duplicate key makes React carry the old
                    // list's bars into the new one when the rows are replaced.
                    <div className="ranked-bars__row" key={`${i}-${row.name}`}>
                        <span className="ranked-bars__name">{row.name}</span>
                        <span className="ranked-bars__value">{row.display}</span>
                        <div
                            className="ranked-bars__track"
                            role="img"
                            aria-label={`${row.name}: ${row.display}`}
                        >
                            <div className="ranked-bars__fill" style={{width: `${percent}%`}} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default RankedBars;
